import React, { useEffect, useRef, useState } from "react";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import { Box, Container } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { useStyles } from "./style";
import { RateAdminAdvanceRateBreadcrumb } from "./CreateRateAdminAdvanceRate/constant";
import { printTableData } from "../../../utils/printUtils";
import ViewInlineRateAdminAdvanceRate from "./ViewInlineRateAdminAdvanceRate";
import { FILTER } from "../../../services/common/urlService";
import { DATE_STRATEGY, formatDate } from "../../../utils/date";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;

const RateAdminAdvanceRate = (props) => {
  const classes = useStyles();
  const {
    fetchRateAdminAdvanceRates,
    tableData,
    exportRateAdminAdvanceRate,
    isPageLevelError,
    paginationInfo,
    isLoading,
    screenPermissions,
    handleCreateRateAdminAdvanceRate,
    deleteRateAdminAdvanceRate,
    viewRateAdminAdvanceRateRoute,
    editRateAdminAdvanceRateRoute,
    savedTableState,
    onTableStateChange,
    shouldOpenDeleteConfirmationPopup,
    setDeleteConfirmationPopupVisibility,
    isRateAdminAdvanceRateDeleteSuccess,
    isRateAdminAdvanceRateDeleteError,
    fetchRateAdminRatesById,
    formFieldValueMap,
    isRateAdminRatesDetailSuccess,
    locationDD,
    vehicleCategoryDD,
    RateCodeDD,
    RateOptionsDD,
    RateNetworkDD,
    fetchRateAdminRatesRateAdjDetails,
    createRateAdminRateAdjustment,
    TestRateAdminRates,
    fetchExternalNetworkDD,
    fetchExternalRateTest,
    clearExternalRateTest,
    rateAdjustmentlist,
    RateTest,
    ExternalNetworkDD,
    ExternalRateTest,
    isExternalRateTestSuccess,
    isExternalRateTestError,
    isRateAdminRateAdjSuccess,
    isRateAdminRateAdjListSuccess,
    editRateAdminRates,
    isRateAdminRatesEditSuccess,
    isRateAdminRatesEditError,
    fetchRevenuePreview,
    revenuePreviewData,
    isRevenuePreviewSuccess,
  } = props;

  const [isCopy, setIsCopy] = useState(false);
  const [isPrintError, setIsPrintError] = useState(false);
  const [areFiltersVisible, toggleFilterState] = useState(true);
  const [expandedRows, setExpandedRows] = useState(savedTableState?.expandedRows || null);
  const [rateDetails, setRateDetails] = useState({});
  const [pendingFetchId, setPendingFetchId] = useState(null);

  const renderProgressBar = () => {
    return <ProgressBar mode="indeterminate" style={{ height: "4px" }} />;
  };

  const convertToTSV = (data) => {
    // Get headers from columns (including ID)
    const headers = data.columns.map((item) => item.colLabel).join("\t");

    // Get rows with values in the same order as columns
    const rows = data.rows
      .map((row) => {
        return data.columns
          .map((col) => {
            const value = row[col.dataKey];
            // Handle null/undefined values
            if (value === null || value === undefined) {
              return "-";
            }
            // Convert to string
            return String(value);
          })
          .join("\t");
      })
      .join("\n");

    return `${headers}\n${rows}`;
  };

  const copyToClipboard = () => {
    const csv = convertToTSV(tableData);
    navigator.clipboard
      .writeText(csv)
      .then(() => setIsCopy(true))
      .catch((err) => setIsCopy(true));
  };

  useEffect(() => {
    setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);

  useEffect(() => {
    if (isPrintError) {
      const timer = setTimeout(() => setIsPrintError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isPrintError]);

  useEffect(() => {
    if (pendingFetchId && isRateAdminRatesDetailSuccess && formFieldValueMap) {
      if (formFieldValueMap.id === pendingFetchId) {
        setRateDetails(prev => ({ ...prev, [pendingFetchId]: formFieldValueMap }));
        setPendingFetchId(null);
        
        // Call fetchRevenuePreview when row is expanded and details are loaded
        if (fetchRevenuePreview) {
          const payload = {
            rateId: formFieldValueMap.id,
            rateBaseDTO: [
              { fkRatePlanId: 1, rate: parseFloat(formFieldValueMap.dailybaseRate) || 0, miles: 0 },
              { fkRatePlanId: 2, rate: parseFloat(formFieldValueMap.weekendbaseRate) || 0, miles: 0 },
              { fkRatePlanId: 3, rate: parseFloat(formFieldValueMap.weeklybaseRate) || 0, miles: 0 },
              { fkRatePlanId: 4, rate: parseFloat(formFieldValueMap.monthlybaseRate) || 0, miles: 0 }
            ]
          };
          fetchRevenuePreview(payload);
        }
      }
    }
  }, [formFieldValueMap, isRateAdminRatesDetailSuccess, pendingFetchId, fetchRevenuePreview]);

  useEffect(() => {
    if (formFieldValueMap && formFieldValueMap.id && expandedRows) {
      const expandedIds = Object.keys(expandedRows);
      if (expandedIds.includes(String(formFieldValueMap.id))) {
        setRateDetails(prev => ({ ...prev, [formFieldValueMap.id]: formFieldValueMap }));
      }
    }
  }, [formFieldValueMap, expandedRows]);

  useEffect(() => {
    if (savedTableState?.expandedRows && fetchRateAdminRatesById) {
      const expandedIds = Object.keys(savedTableState.expandedRows);
      for (const rowId of expandedIds) {
        const numericId = parseInt(rowId, 10);
        if (numericId && !rateDetails[numericId]) {
          setPendingFetchId(numericId);
          fetchRateAdminRatesById({ id: numericId });
          if (fetchRateAdminRatesRateAdjDetails) {
            fetchRateAdminRatesRateAdjDetails({ id: numericId });
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to build fetch params from saved state (preserves filters)
  const buildFetchParamsFromSavedState = (state) => {
    if (!state) return { sortField: 'id', userId: localStorage.getItem("userId") };
    
    // Helper to check if a string looks like an ISO date
    const isISODateString = (str) => {
      if (typeof str !== 'string') return false;
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
    };
    
    // Helper to format date values to YYYY-MM-DD
    const formatFilterValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value, DATE_STRATEGY.DATE_ISO);
      }
      if (isISODateString(value)) {
        return formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
      }
      return value;
    };
    
    // Build filter params in the format expected by the API
    const filters = [];
    if (state.filters) {
      Object.keys(state.filters).forEach(key => {
        if (key !== 'global' && state.filters[key]?.value !== null && state.filters[key]?.value !== undefined) {
          const filterValue = state.filters[key].value;
          filters.push({
            filterField: state.filters[key].filterKey || key,
            matchStrategy: state.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: formatFilterValue(filterValue)
          });
        }
      });
    }
    
    return {
      sortField: state.sortField || 'id',
      sortOrder: state.sortOrder,
      pageNumber: state.page || 0,
      pageSize: state.rows || 10,
      filters: filters.length > 0 ? filters : undefined,
      userId: localStorage.getItem("userId")
    };
  };

  // Refresh grid data after successful edit
  useEffect(() => {
    if (isRateAdminRatesEditSuccess) {
      if (fetchRateAdminAdvanceRates) {
        // Refresh the grid data with preserved filters and pagination
        const params = buildFetchParamsFromSavedState(savedTableState);
        fetchRateAdminAdvanceRates(params);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRateAdminRatesEditSuccess]);

  // Refresh grid data after successful delete
  useEffect(() => {
    if (isRateAdminAdvanceRateDeleteSuccess) {
      if (fetchRateAdminAdvanceRates) {
        // Refresh the grid data with preserved filters and pagination
        const params = buildFetchParamsFromSavedState(savedTableState);
        fetchRateAdminAdvanceRates(params);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRateAdminAdvanceRateDeleteSuccess]);

  const handlePrint = () => {
    const success = printTableData({
      title: "Advance Rate",
      columns: tableData?.columns || [],
      rows: tableData?.rows || [],
    });
    if (!success) {
      setIsPrintError(true);
    }
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Advance Rate</h4>
            <Button
              label={areFiltersVisible ? "Advanced Filter" : "Advanced Filter"}
              icon={areFiltersVisible ? "pi pi-filter-slash" : "pi pi-filter"}
              className={`p-button-text p-button-secondary shadow-btn`}
              onClick={() => {
                toggleFilterState(!areFiltersVisible);
              }}
            />
          </div>

          <div className="buttonarea">
            {screenPermissions?.create && (
              <div className="create-btn-group">
                <Button
                  label={"Create Advance Rate"}
                  className={`p-button-raised`}
                  style={{ borderRadius: "0px" }}
                  onClick={handleCreateRateAdminAdvanceRate}
                />
              </div>
            )}
            <div className="copy-excel-print-group">
              <Button
                label={"Update Advance Rate"}
                className={`p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={editRateAdminAdvanceRateRoute}
              />
              <Button
                label={"Copy"}
                className={`p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={copyToClipboard}
              />
              <Button
                label={"Excel"}
                className={`p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={exportRateAdminAdvanceRate}
              />
              <Button
                label={"Print"}
                className={`p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderComplianceRecords = () => tableData?.columns?.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderComplianceRecords() && !shouldRenderFullPageError();

  // Build initial filters from saved state if available
  const getInitialFilters = () => {
    const defaultFilters = {
      global: { value: null },
      locationTitle: { value: null, filterKey: "locationTitle" },
      categoryTitle: { value: null, filterKey: "categoryTitle" },
      startDate: { value: null, filterKey: "startDate" },
      endDate: { value: null, filterKey: "endDate" },
    };

    // If we have saved filters, merge them with defaults
    if (savedTableState?.filters) {
      const savedFilters = savedTableState.filters;
      Object.keys(savedFilters).forEach(key => {
        if (defaultFilters[key]) {
          defaultFilters[key] = { ...defaultFilters[key], ...savedFilters[key] };
        }
      });
    }

    return defaultFilters;
  };

  const filters = getInitialFilters();

  const getToastProps = () => {
    if (isPrintError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle:
          "Unable to open print window. Please check your popup blocker settings.",
        shouldShowToast: true,
      };
    }
    if (isCopy) {
      let toastTitle = "Data copied to clipboard!";
      if (!isCopy) {
        toastTitle = "Error while Data copied to clipboard!";
      }

      return {
        severity: isCopy ? TOAST_SEVERITY.SUCCESS : TOAST_SEVERITY.ERROR,
        toastTitle,
        shouldShowToast: true,
      };
    }
  };

  const rowExpansionTemplate = (data) => {
    const details = rateDetails[data.id];
    const isLoadingDetails = isLoading && pendingFetchId === data.id;

    if (isLoadingDetails || !details) {
      return (
        <Box sx={{ backgroundColor: '#f8f9fa', padding: 0, margin: 0, '& .p-progressbar': { margin: 0 } }}>
          {renderProgressBar()}
        </Box>
      );
    }

    return (
      <ViewInlineRateAdminAdvanceRate
        formFieldValueMap={details}
        isEdit={true}
        isLoading={isLoadingDetails}
        editRateAdminRates={editRateAdminRates}
        RateCodeDD={RateCodeDD}
        locationDD={locationDD}
        vehicleCategoryDD={vehicleCategoryDD}
        RateOptionsDD={RateOptionsDD}
        RateNetworkDD={RateNetworkDD}
        createRateAdminRateAdjustment={createRateAdminRateAdjustment}
        TestRateAdminRates={TestRateAdminRates}
        fetchExternalNetworkDD={fetchExternalNetworkDD}
        fetchExternalRateTest={fetchExternalRateTest}
        clearExternalRateTest={clearExternalRateTest}
        rateAdjustmentlist={rateAdjustmentlist}
        RateTest={RateTest}
        ExternalNetworkDD={ExternalNetworkDD}
        ExternalRateTest={ExternalRateTest}
        isExternalRateTestSuccess={isExternalRateTestSuccess}
        isExternalRateTestError={isExternalRateTestError}
        isRateAdminRateAdjSuccess={isRateAdminRateAdjSuccess}
        isRateAdminRateAdjListSuccess={isRateAdminRateAdjListSuccess}
        isRateAdminRatesEditSuccess={isRateAdminRatesEditSuccess}
        isRateAdminRatesEditError={isRateAdminRatesEditError}
        shouldBlockNavigation={() => {}}
        isInlineView={true}
        fetchRevenuePreview={fetchRevenuePreview}
        revenuePreviewData={revenuePreviewData}
        isRevenuePreviewSuccess={isRevenuePreviewSuccess}
      />
    );
  };

  const handleRowToggle = (e) => {
    setExpandedRows(e.data);
    
    if (onTableStateChange) {
      onTableStateChange({ expandedRows: e.data });
    }
    
    if (e.data && fetchRateAdminRatesById) {
      const expandedIds = Object.keys(e.data);
      
      for (const rowId of expandedIds) {
        const numericId = parseInt(rowId, 10);
        if (numericId && !rateDetails[numericId] && pendingFetchId !== numericId) {
          setPendingFetchId(numericId);
          fetchRateAdminRatesById({ id: numericId });
          if (fetchRateAdminRatesRateAdjDetails) {
            fetchRateAdminRatesRateAdjDetails({ id: numericId });
          }
        }
      }
    }
  };

  const tableProps = {
    areFiltersVisible,
    columns: tableData?.columns,
    paginationInfo,
    screenPermissions,
    value: tableData?.rows,
    emptyMessage: "No Advance Rate found.",
    filters,
    loadLazyData: fetchRateAdminAdvanceRates,
    loading: isLoading,
    showMoreActions: false,
    sortField: savedTableState?.sortField || 'id',
    initialSortOrder: savedTableState?.sortOrder,
    initialFirst: savedTableState?.first || 0,
    initialRows: savedTableState?.rows || 10,
    onTableStateChange,
    setDeleteConfirmationPopupVisibility,
    shouldOpenDeleteConfirmationPopup,
    onDeleteRecord: deleteRateAdminAdvanceRate,
    showViewButton: false,
    showCopyButton: false,
    showDeleteButton: true,
    showEditButton: false,
    expandedRows,
    onRowToggle: handleRowToggle,
    rowExpansionTemplate,
  };

  return (
    <>
      <Container maxWidth="lg">
        <Breadcrumb list={RateAdminAdvanceRateBreadcrumb} />
        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message={"Oops! Something broke, we are working on a fix."}
              />
            )}
            {shouldRenderComplianceRecords() && (
              <Box className={classes.ratesTableWrapper}>
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={"No Advance Rate records to show"}
              />
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default RateAdminAdvanceRate;
